import { css } from 'styled-components';

import { BaseStyledElement } from './BaseStyledElements';
import { BaseStyledModifier } from './BaseStyledModifiers';

export const BaseStyled = {
  /**
   * Base styling for Banners
   *
   * ```css
   * :where(.row) {}
   * :where(.col) {}
   * :where(.contentBlock) {}
   * :where(.contentBlock-picture) {
   * --contentBlock-aspect-ratio
   * }
   * :where(.contentBlock-picture-image) {}
   * :where(.contentBlock-body) {}
   * :where(.contentBlock-bodyAlign) {
   * --contentBlock-content-padding
   * }
   * ```
   *
   */
  Banner: css`
    :where(.row) {
      ${BaseStyledElement.row}
    }

    :where(.col:only-child) {
      ${BaseStyledElement.col}
    }

    :where(.contentBlock) {
      ${BaseStyledElement.contentBlock}
      ${BaseStyledModifier.Layout}
      ${BaseStyledModifier.ColorSchemes}
      ${BaseStyledModifier.TextShadow}
      ${BaseStyledModifier.WrapButtons}
    }

    :where(.contentBlock-picture) {
      ${BaseStyledElement.contentBlockPicture}
      aspect-ratio: var(--contentBlock-aspect-ratio, 16/9);
    }

    :where(.contentBlock-picture-image) {
      ${BaseStyledElement.contentBlockPictureImage}
    }

    :where(.contentBlock-body) {
      ${BaseStyledElement.contentBlockBody}
    }

    :where(.contentBlock-bodyAlign) {
      ${BaseStyledElement.contentBlockBodyAlign}
    }

    :where(.contentBlock-body-picture) {
      ${BaseStyledElement.contentBlockBodyPicture}
    }

    :where(.contentBlock-body-picture-image) {
      ${BaseStyledElement.contentBlockBodyPictureImage}
    }

    :where(.contentBlock-body-links) {
      ${BaseStyledElement.contentBlockBodyLinks}
    }

    :where(.contentBlock-body-link) {
      ${BaseStyledElement.contentBlockBodyLink}
    }

    /* after the body-link block: source order decides the cascade (:where) */
    /* stylelint-disable-next-line no-duplicate-selectors */
    :where(.contentBlock) {
      ${BaseStyledModifier.ButtonVariants}
    }
  `,
  /**
   * Base styling for ContentBlock
   *
   * ```css
   * :where(.contentBlock) {}
   * :where(.contentBlock-picture) {}
   * :where(.contentBlock-picture-image) {}
   * :where(.contentBlock-body) {}
   * :where(.contentBlock-bodyAlign) {
   * --contentBlock-content-padding
   * }
   *
   * :where(.contentBlock-body-link) {
   * --contentBlock-button-border-radius
   * --contentBlock-button-border-color
   * --contentBlock-button-background-color
   * --contentBlock-button-text-color
   * --contentBlock-button-hover-border-color
   * --contentBlock-button-hover-background-color
   * --contentBlock-button-hover-text-color
   * --contentBlock-button-active-border-color
   * --contentBlock-button-active-background-color
   * --contentBlock-button-active-text-color
   * }
   * ```
   *
   */
  ContentBlock: css`
    :where(.contentBlock) {
      ${BaseStyledElement.contentBlock}
      ${BaseStyledModifier.Layout}
      ${BaseStyledModifier.ColorSchemes}
      ${BaseStyledModifier.TextShadow}
      ${BaseStyledModifier.WrapButtons}
    }

    :where(.contentBlock-picture) {
      ${BaseStyledElement.contentBlockPicture}
    }

    :where(.contentBlock-picture-image) {
      ${BaseStyledElement.contentBlockPictureImage}
    }

    :where(.contentBlock-body) {
      ${BaseStyledElement.contentBlockBody}
    }

    :where(.contentBlock-bodyAlign) {
      ${BaseStyledElement.contentBlockBodyAlign}
    }

    :where(.contentBlock-body-picture) {
      ${BaseStyledElement.contentBlockBodyPicture}
    }

    :where(.contentBlock-body-picture-image) {
      ${BaseStyledElement.contentBlockBodyPictureImage}
    }

    :where(.contentBlock-body-links) {
      ${BaseStyledElement.contentBlockBodyLinks}
    }

    :where(.contentBlock-body-link) {
      ${BaseStyledElement.contentBlockBodyLink}
    }

    /* after the body-link block: source order decides the cascade (:where) */
    /* stylelint-disable-next-line no-duplicate-selectors */
    :where(.contentBlock) {
      ${BaseStyledModifier.ButtonVariants}
    }
  `,
};

export const { Banner, ContentBlock } = BaseStyled;
